import {
  toolCallResultSchema,
  ToolCallResultStatus,
} from 'common/src/modules/creativeTemplates/copilot/copilotCreativeTemplates.consts';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import updateLayer from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/updateLayer';
import {
  AiAnimatorRangeBase,
  AnimatorRangeBase,
  AnimatorRangeUnit,
} from 'common/src/modules/movie/movie.enums';
import {AiTrackingType, TrackingType} from 'common/src/modules/movie/movieLayers.enums';
import {MovieTextAnimator} from 'common/src/modules/movie/movieLayers.types';
import {NumberArr} from 'common/src/modules/movie/moviePrimitives.types';
import simpleMovieValue from 'common/src/modules/movie/utils/simpleMovieValue';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import invokeStandaloneLLMCall from 'common/src/modules/shared/copilot/utils/invokeStandaloneLLMCall';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {z} from 'zod';
import {buildKeyframedValue} from '../../../utils/buildKeyframedValue';
import {convertTextAnimatorsToSchema} from '../../utils/convertTextAnimatorsToSchema';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';
import {textAnimatorSchema} from '../adBuilderAgent.schemas';
import {AnimatedKeyframeInput} from '../adBuilderAgent.types';

// Schema for the tool input
const inputSchema = z.object({
  layerId: z.string().describe('ID of the text layer to edit animators for'),
  editDescription: z
    .string()
    .describe('Natural language description of the text animation changes to make'),
});

function addOrEditTextAnimatorTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.AddOrEditTextAnimator,
    displayName: 'Editing text animator',
    description: `Apply or edit existing text animator properties using natural language. 
    The tool will analyze current animators and apply what is needed.
    Text animator include ability to apply opacity/ color/ position shift/ tracking styling on range of characters/words/lines where the range start,end/offset can be animated over time (e.g., per-character fades or word-by-word reveals).
    For fixed formatting, use the standard content editing tool.`,
    inputSchema,
    execute: async ({
      input,
      abortSignal,
    }: ToolExecuteParams<typeof inputSchema>): Promise<{content: string}> => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
        };
      }

      try {
        const layer = activeComp.l.find((l) => l.id === input.layerId);

        if (!layer) {
          return {
            content: `ERROR: Layer with ID "${input.layerId}" not found`,
          };
        }

        if (layer.isLocked === true) {
          return {
            content: `ERROR: Layer "${input.layerId}" is locked and cannot be modified`,
          };
        }

        if (layer.ty !== 'Text') {
          return {
            content: `ERROR: Layer "${input.layerId}" is not a text layer`,
          };
        }

        // Get existing text animators
        const existingAnimators = layer.text.animators || [];
        const existingAnimatorsSchema = convertTextAnimatorsToSchema(existingAnimators);

        // Prepare context for LLM
        const existingAnimatorsContext =
          existingAnimatorsSchema.length > 0
            ? `Current text animators:\n${JSON.stringify(existingAnimatorsSchema, null, 2)}`
            : 'No existing text animators';

        const systemPrompt = [
          'Text animator (in graphic design) include ability to apply opacity/ color/ position shift/ tracking styling on range of characters/words/lines where the range can be animated over time (e.g., per-character fades or word-by-word reveals).',
          'You are requested to edit text animator properties based on natural language description.',
          'The user will provide a description of what they want or changes they want to make on existing text animator.',
          'IMPORTANT: while the style property can be keyframed and animate as well, animate only the range selector start, end or offset and do not apply animation on the style properties, unless EXPLICITLY request by the user.',
          'IMPORTANT: Return the complete new animator configuration, not just the changes.',
          'IMPORTANT: At least one style (opacity/ color/ position shift/ tracking) must be present, and re-iterating that they should not be animated unless explicitly requested by the user (they should have single keyframe).',
          'You should return the NEW complete text animator configuration that should replace the existing one.',
          'IMPORTANT: Return the complete new animator configuration, not just the changes.',
          'If the user wants to remove all animations, return an empty object.',
          'Consider the existing animators as context but provide the full new configuration.',
        ].join(' ');

        const userPrompt = `${existingAnimatorsContext}

Edit request: "${input.editDescription}"

Based on the existing text animators and the edit request, provide the complete new text animator configuration.
Return the full animator object that should replace the existing one.`;

        const response = await invokeStandaloneLLMCall({
          messages: [openAISystemMessage(systemPrompt), openAIUserMessage(userPrompt)],
          responseSchema: toolCallResultSchema.extend({
            result: textAnimatorSchema,
          }),
          buildResponseUsingToolCall: true,
          abortSignal,
        });

        // Handle different response statuses
        if (response.status === ToolCallResultStatus.Failure) {
          const reason = response.failureReason || 'Request failed without specific reason';

          return {
            content: `ERROR: Text animator edit failed: ${reason}`,
          };
        }

        // Convert the LLM response back to MovieTextAnimator format
        const animatorParams = response.result as z.infer<typeof textAnimatorSchema>;

        // Check if the response is empty (no text animator needed)
        const isEmptyAnimator =
          !animatorParams.opacity &&
          !animatorParams.color &&
          !animatorParams.position &&
          !animatorParams.tracking &&
          !animatorParams.rangeSelector;

        if (isEmptyAnimator) {
          // Update the layer with empty animators array
          store.dispatch(
            updateLayer({
              layerId: input.layerId,
              data: {
                text: {
                  ...layer.text,
                  animators: [],
                },
              },
            }),
          );

          return {
            content: `Removed all text animators from layer "${input.layerId}"`,
          };
        }

        // Create new text animator from the LLM response
        const newTextAnimator: MovieTextAnimator = {
          opacity: animatorParams.opacity
            ? (buildKeyframedValue(animatorParams.opacity as AnimatedKeyframeInput[], 1, {
                isOpacity: false, // we set this to false even though its opacity, since this property shouldnt be kept with mult=0.01
                propertyName: 'Opacity',
              }) as NumberArr<1>)
            : undefined,
          fillColor: animatorParams.color
            ? (buildKeyframedValue(animatorParams.color as AnimatedKeyframeInput[], 3, {
                propertyName: 'Fill Color',
              }) as NumberArr<3>)
            : undefined,
          position: animatorParams.position
            ? (buildKeyframedValue(animatorParams.position as AnimatedKeyframeInput[], 2, {
                propertyName: 'Position',
              }) as NumberArr<2>)
            : undefined,
          tracking: animatorParams.tracking
            ? {
                trackingAmount: buildKeyframedValue(
                  animatorParams.tracking.amount as AnimatedKeyframeInput[],
                  1,
                  {
                    propertyName: 'Tracking Amount',
                  },
                ) as NumberArr<1>,
                trackingType: simpleMovieValue([
                  getMatchingEnumValue(
                    AiTrackingType,
                    TrackingType,
                    animatorParams.tracking.type,
                  ) || TrackingType.Before,
                ]),
              }
            : undefined,
          rangeSelectors: animatorParams.rangeSelector
            ? [
                {
                  start: buildKeyframedValue(
                    animatorParams.rangeSelector.start as AnimatedKeyframeInput[],
                    1,
                    {
                      propertyName: 'Range Selector Start',
                    },
                  ) as NumberArr<1>,
                  end: buildKeyframedValue(
                    animatorParams.rangeSelector.end as AnimatedKeyframeInput[],
                    1,
                    {
                      propertyName: 'Range Selector End',
                    },
                  ) as NumberArr<1>,
                  offset: buildKeyframedValue(
                    animatorParams.rangeSelector.offset as AnimatedKeyframeInput[],
                    1,
                    {
                      propertyName: 'Range Selector Offset',
                    },
                  ) as NumberArr<1>,
                  units: AnimatorRangeUnit.Percent,
                  smoothness: animatorParams.rangeSelector.smoothness,
                  basedOn:
                    getMatchingEnumValue(
                      AiAnimatorRangeBase,
                      AnimatorRangeBase,
                      animatorParams.rangeSelector.basedOn,
                    ) || AnimatorRangeBase.Char,
                },
              ]
            : [],
        };

        // Update the layer with the new animator (replacing all existing animators)
        store.dispatch(
          updateLayer({
            layerId: input.layerId,
            data: {
              text: {
                ...layer.text,
                animators: [newTextAnimator],
              },
            },
          }),
        );

        const resultMessage = `Updated text animator for layer "${input.layerId}"`;
        const unsupportedMessage = response.unsupportedRequestPart
          ? ` (Note: ${response.unsupportedRequestPart})`
          : '';

        return {
          content: resultMessage + unsupportedMessage,
        };
      } catch (error) {
        return {
          content: `Failed to edit text animator: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    },
  });
}

export default addOrEditTextAnimatorTool;
